import type { ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import styles from '../igrow.module.css';

export type FieldControlProps = {
  id: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  className: string;
};

type FormFieldProps = {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  variant?: 'input' | 'textarea' | 'select';
  children: (props: FieldControlProps) => ReactNode;
};

export default function FormField({
  id,
  label,
  error,
  required = false,
  optional = false,
  variant = 'input',
  children,
}: FormFieldProps) {
  const errorId = `${id}-error`;
  const controlClass = [
    styles.control,
    variant === 'textarea' ? styles.textarea : styles.input,
    variant === 'select' ? styles.select : '',
    error ? styles.controlError : '',
  ]
    .filter(Boolean)
    .join(' ');

  const control = children({
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? errorId : undefined,
    className: controlClass,
  });

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <>
            {' '}
            <span className={styles.asterisk} aria-hidden="true">
              *
            </span>
            <span className={styles.visuallyHidden}> required</span>
          </>
        ) : null}
        {optional ? <span className={styles.optional}> (Optional)</span> : null}
      </label>
      {variant === 'select' ? (
        <div className={styles.selectWrap}>
          {control}
          <ChevronDown className={styles.chevron} aria-hidden="true" />
        </div>
      ) : (
        control
      )}
      {error ? (
        <p className={styles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
